import * as React from 'react';
import { useReactToPrint } from 'react-to-print';
import { ActionPlanView, ActionPointData, permissionGroupHelper, PublicActionPlanLink, PublicAmorgUser, rng } from '../../../amorg-common';
import { fieldValidation } from '../../../amorg-common/modules/fieldValidation/fieldValidation';
import { assignActionPlanOwner, createActionPlanTopic, deleteActionPlan, getActionPlanLinks, getActionPlanViewById, updateActionPlan } from '../../../api/actionPlanClientApi';
import { getActivity, updateActivity } from '../../../api/activityClientApi';
import { Label } from '../../../designSystem/Label';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { Button } from '../../../designSystem/button/Button';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { EditableTextArea } from '../../../designSystem/compositeComponents/EditableTextArea';
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { UserSelectorButton } from '../../../designSystem/compositeComponents/itemSelector/UserSelectorAvatarButton';
import { StartEndDateFields } from '../../../designSystem/inputs/StartEndDateFields';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { PageHeader } from '../../../designSystem/PageHeader';
import { ToolBarButton, ToolBarContainer } from '../../../designSystem/compositeComponents/ButtonToolBar';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { ActionPlanContextPanel } from './ActionPlanContextPanel';
import { ActionPlanLinkEditor } from './ActionPlanLinkEditor';
import { SurveyTopicListButton } from './SurveyTopicListButton';
import { ActionPlanTopicList } from './ActionPlanTopicList';
import PrintActionPlanView from './PrintActionPlanPage';
import { PageSection } from '../../../designSystem/PageSection';
import { CommentDrawer } from '../../../designSystem/compositeComponents/CommentsDrawer';
import { DeleteConfirmationModal } from '../../../designSystem/compositeComponents/DeleteConfirmationModal';
import { Card } from '../../../designSystem/Card';
import { StatusIconWithText } from '../../../designSystem/compositeComponents/compositeIcons/StatusIconWithText';
import { FormField } from '../../../designSystem/formField/FormField';
import { Divider } from '../../../designSystem/Divider';
import { ResponsiveGrid, ResponsiveGridItem } from '../../../designSystem/ResponsiveGrid';
import { FileLinkCommentsButtonRow } from '../../../designSystem/compositeComponents/FileLinkCommentsButtonRow';

type State = {
    actionPlan: ActionPlanView | null,
    links: PublicActionPlanLink[];
};

type ActionPlanPageViewProps = {
    actionPlanId: string,
    onBack?: () => void;
    onTitleChanged?: (title: string) => void;
};
export default function ActionPlanPageView (props: ActionPlanPageViewProps): JSX.Element
{
    const [state, setState] = React.useState<State>({
        actionPlan: null,
        links: []
    });
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [showCommentsDrawer, setShowCommentsDrawer] = React.useState(false);

    React.useEffect(function ()
    {
        const getData = async () =>
        {
            await reloadFullState();
            setLoading(false);
        };
        getData();
    }, [props.actionPlanId]);

    const activeUser = React.useContext(ProtectedPageContext).user;

    async function reloadFullState ()
    {

        const actionPlan = await getActionPlanViewById(props.actionPlanId);
        if (!actionPlan)
            return;

        const links = await getActionPlanLinks(actionPlan.id);

        const newState: State = {
            actionPlan,
            links
        };

        setState(newState);
        props.onTitleChanged?.(actionPlan.title);

        return newState;
    }

    async function reloadActionPlan ()
    {
        setLoading(true);
        const actionPlan = await getActionPlanViewById(props.actionPlanId);

        setState({
            ...state,
            actionPlan
        });
        setLoading(false);
    }


    async function handleSaveTitle (value: string)
    {
        if (!state.actionPlan)
            return;

        setLoading(true);
        if ((await updateActionPlan(state.actionPlan.id, value, state.actionPlan.description)).isSuccess)
            reloadFullState();
        setLoading(false);
    }

    async function handleSaveDescription (value: string)
    {
        if (!state.actionPlan)
            return;

        setLoading(true);
        if ((await updateActionPlan(state.actionPlan.id, state.actionPlan.title, value)).isSuccess)
            reloadFullState();
        setLoading(false);
    }

    async function handleClickCreateTopic ()
    {
        if (!state.actionPlan)
            return;

        setLoading(true);
        if ((await createActionPlanTopic(rng.getRandomId(), state.actionPlan.id)).isSuccess)
            reloadActionPlan();
        setLoading(false);
    }

    async function handleDateChange (start: Date, end: Date)
    {
        if (!state.actionPlan)
            return;

        setLoading(true);
        const activity = await getActivity(state.actionPlan.activityId);
        if (activity)
        {
            const result = await updateActivity(activity.id, activity.title, activity.description, start, end, activity.location);
            if (result.isSuccess)
            {
                reloadFullState();
            }
        }
        setLoading(false);
    }

    async function handleChangeOwner (user: PublicAmorgUser | null)
    {
        const result = await assignActionPlanOwner(props.actionPlanId, user?.id || '');
        if (result.isSuccess)
        {
            reloadFullState();
        }
    }

    async function handleClickDeleteActionPlan ()
    {
        if (!state.actionPlan)
            return;

        setLoading(true);
        const deleteResult = await deleteActionPlan(state.actionPlan.id);
        if (deleteResult.isSuccess)
            props.onBack?.();
        setLoading(false);
    }

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: state.actionPlan?.title || "Handleplan"
    });

    const planIsLoaded = state.actionPlan !== null;

    const isMember = activeUser && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroupId, 'member');
    const canSelectOwner = planIsLoaded && isMember;

    const handleShowDeleteModal = React.useCallback(() =>
    {
        setShowDeleteModal(true);
    }, []);

    const allActionPoints: ActionPointData[] = state.actionPlan?.topics.flatMap(t => t.actionPoints) || [];

    return (
        <>
            {!state.actionPlan &&
                <LoadingPageOverlay loading={loading} />
            }

            {state.actionPlan &&
                <>
                    <PageHeader
                        headerText="handleplan"
                        buttons={
                            <ToolBarContainer>
                                <ToolBarButton
                                    icon="interface_actions_print"
                                    tooltip="Print"
                                    variant="text"
                                    onClick={printFn}
                                />
                                {isMember &&
                                    <ToolBarButton
                                        icon="interface_actions_delete"
                                        tooltip="Slet handleplan"
                                        variant="destructive-text"
                                        onClick={handleShowDeleteModal}
                                    />
                                }
                            </ToolBarContainer>
                        }
                    />

                    <PrintActionPlanView
                        actionPlan={state.actionPlan}
                        innerRef={printRef} />

                    <SingleColumnLayout>
                        <Card variant="outlined">
                            <Flex flexDirection='column' gap={16}>

                                <Flex flexWrap="wrap">
                                    <FlexItem flexGrow='1'>
                                        <EditableText
                                            value={state.actionPlan.title}
                                            placeholder='handleplanens titel'
                                            ariaLabel="handleplanens titel"
                                            styleMode='H2'
                                            validator={(currentValue) =>
                                                fieldValidation.activity.title(currentValue)
                                                    ? { valid: true }
                                                    : { valid: false, errorMessage: `Ugyldig titel. Skal udfyldes med maks. 100 (${currentValue.length}) tegn.` }
                                            }
                                            onSave={(value) => fieldValidation.activity.title(value) && handleSaveTitle(value)}
                                        />
                                    </FlexItem>

                                    <Flex alignItems="center" gap={16}>

                                        <ActionPointBadges allActionPoints={allActionPoints} />

                                        {activeUser &&
                                            <ActionPlanLinkEditor
                                                siteId={activeUser.siteId}
                                                actionPlanId={state.actionPlan.id}
                                                linkContext={{ type: 'actionPlan' }}
                                                link={state.links.find(l => l.configuration.type === 'actionPlan')}
                                                onUpdated={() => reloadFullState()}
                                            />
                                        }
                                    </Flex>
                                </Flex>

                                <Divider />

                                <ResponsiveGrid rowGap={16} columnGap={16}>

                                    <ResponsiveGridItem desktop={6} tablet={12} mobile={12}>
                                        <FormField label="Formål" style={{ flex: '1 1 auto', maxWidth: '100%', minWidth:'200px' }} 
                                            inputComponent={
                                                <EditableTextArea
                                                    ariaLabel='Beskrivelse'
                                                    placeholder='Indsæt beskrivelse...'
                                                    value={state.actionPlan.description}
                                                    validator={(currentValue) =>
                                                    {
                                                        return fieldValidation.actionPlan.description(currentValue)
                                                            ? { valid: true }
                                                            : { valid: false, errorMessage: `Ugyldig titel. Maks. 200 (${currentValue.length}) tegn.` };
                                                    }}
                                                    onSave={(value, isValid) =>
                                                    {
                                                        if (isValid)
                                                            handleSaveDescription(value);
                                                    }}
                                                />
                                            }
                                        />
                                    </ResponsiveGridItem>

                                    <ResponsiveGridItem desktop={2} tablet={6} mobile={12}>
                                        <Card variant="outlined" style={{ height: '104px' }}>
                                            <FormField
                                                label="Ansvarlig"
                                                inputComponent={
                                                    <UserSelectorButton
                                                        display='avatar-card-no-border'
                                                        disabled={!canSelectOwner}
                                                        selectedUserId={state.actionPlan.owner?.userId || ''}
                                                        onUserChange={handleChangeOwner}
                                                    />
                                                }
                                            />
                                        </Card>
                                    </ResponsiveGridItem>

                                    <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>

                                        <Card variant="outlined">
                                            <Flex flexDirection={"column"} gap={4}>
                                                <div style={{ minHeight: '24px' }}><Label formIndent>Periode</Label></div>
                                                <StartEndDateFields noBorder
                                                    disabled={!isMember}
                                                    start={state.actionPlan.start}
                                                    end={state.actionPlan.end}
                                                    onChanged={handleDateChange}
                                                />
                                            </Flex>
                                        </Card>
                                    </ResponsiveGridItem>

                                </ResponsiveGrid>

                                <Divider />

                                {isMember &&
                                    <FileLinkCommentsButtonRow
                                        fileReference={{ type: 'ActionPlan', id: state.actionPlan.id }}
                                        linkReference={{ type: 'ActionPlan', id: state.actionPlan.id }}
                                        commentReference={{ type: 'ActionPlan', id: state.actionPlan.id }}
                                    />
                                }

                            </Flex>
                        </Card>
                        
                        <ActionPlanContextPanel
                            actionPlan={state.actionPlan}
                            onUpdated={reloadFullState} />

                        {state.actionPlan.topics.length > 0 &&

                            <PageSection headerStyleVariant='minimal' contentStyleVariant='minimal'
                                header={{
                                    title: "Handleplanens emner",
                                    avatar: { text:state.actionPlan.topics.length.toString() },
                                    tooltipHorizontalAlignment: 'left',
                                    defaultCollapsed: false
                                }}
                                contentPadding='0px'>

                                <ActionPlanTopicList
                                    siteId={activeUser?.siteId || ''}
                                    actionPlan={state.actionPlan}
                                    links={state.links}
                                    onUpdated={reloadFullState}
                                />
                            </PageSection>
                        }

                        <ButtonRow>
                            {state.actionPlan?.type === 'WPA' &&
                                state.actionPlan.surveyData.surveyAnswerCount > 0 &&
                                <SurveyTopicListButton
                                    actionPlanId={state.actionPlan.id}
                                    segmentFilter={state.actionPlan.customData.segmentFilter}
                                    onUpdated={reloadFullState}
                                />
                            }

                            <Button variant='outlined' text={"Opret emne"} icon="actions_plus" size={"42px"} onClick={handleClickCreateTopic} />

                        </ButtonRow>

                        <CommentDrawer
                            visible={showCommentsDrawer}
                            reference={{ type: 'ActionPlan', id: props.actionPlanId }}
                            onClose={() => setShowCommentsDrawer(false)}
                        />


                        <DeleteConfirmationModal
                            showModal={showDeleteModal}
                            buttonText="Slet handleplan"
                            confirmationHeading="Slet handleplan?"
                            confirmationText='Er du sikker på at du vil slette handleplanen?'
                            requireKey={true}
                            onConfirmDelete={handleClickDeleteActionPlan}
                            onCancel={() => setShowDeleteModal(false)} />
                    </SingleColumnLayout>
                </>
            }
        </>
    );
}

function ActionPointBadges (props: {
    allActionPoints: ActionPointData[];
}): JSX.Element
{
    return (
        <Card variant="lightGray" padding='8px 16px'>
            <Flex gap={16}>
                <StatusIconWithText text={`${props.allActionPoints.filter(ap => ap.done).length}`}
                    variant="success"
                    tooltip="Fuldførte handlepunkter"
                />

                <StatusIconWithText text={`${props.allActionPoints.filter(ap => !ap.done && (ap.deadline === null || ap.deadline > new Date())).length}`}
                    variant="pending"
                    tooltip="Åbne handlepunkter"
                />

                <StatusIconWithText text={`${props.allActionPoints.filter(ap => !ap.done && ap.deadline !== null && ap.deadline <= new Date()).length}`}
                    variant="warning"
                    tooltip="Handlepunkter med overskredet deadline"
                />
            </Flex>
        </Card>
    );
}